import React from "react";
import { useGetAggregatedStrategyStatsQuery } from "../api";
import { AggregatedStrategyStat } from "../api/admin";

interface InvestmentStrategiesAggregatedStatsProps {
    skip?: boolean;
}

export const InvestmentStrategiesAggregatedStats: React.FC<InvestmentStrategiesAggregatedStatsProps> = ({
    skip = false,
}) => {
    const { data, isLoading, refetch } = useGetAggregatedStrategyStatsQuery(undefined, { skip });

    const stats = data?.data || [];

    if (isLoading) {
        return (
            <div className="mb-6 p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm animate-pulse">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
                <div className="space-y-3">
                    <div className="h-8 bg-gray-100 dark:bg-gray-700 rounded"></div>
                    <div className="h-8 bg-gray-100 dark:bg-gray-700 rounded"></div>
                    <div className="h-8 bg-gray-100 dark:bg-gray-700 rounded"></div>
                </div>
            </div>
        );
    }

    if (stats.length === 0 && !isLoading) {
        return null;
    }

    return (
        <div className="mb-8 overflow-hidden bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-100 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center bg-gray-50/50 dark:bg-gray-900/50">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Статистика за стратегиями
                </h3>
                <button 
                    onClick={() => refetch()}
                    className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Обновить
                </button>
            </div>
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead>
                        <tr className="bg-gray-50 dark:bg-gray-900/80">
                            <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Стратегия
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Текущий %
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Недель работает
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Средний %
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {stats.map((item: AggregatedStrategyStat) => (
                            <tr key={item.strategy_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center">
                                        <div className="flex-shrink-0 h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 font-bold text-xs mr-3">
                                            {item.strategy_id}
                                        </div>
                                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                                            {item.strategy_name}
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-center">
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                                        {item.current_percent.toFixed(2)}%
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-600 dark:text-gray-300">
                                    {item.weeks_active}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-center">
                                    <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                        {item.average_percent.toFixed(2)}%
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
